import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPatch, apiDel, apiUpload } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, ChevronDown, ChevronRight, Upload, Trash2, Pencil, ShieldCheck, Info } from 'lucide-react';
import { formatINR } from '../../lib/format';
import {
  INSURANCE_TYPES, CANONICAL_POLICY_TYPE, policyTypeToTab, fmtDate,
  DaysBadge, StatusBadge, AiBadge, EnforcedBadge, BTN_PRIMARY, BTN_SECONDARY, normalizeProjects,
} from './complianceShared';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const SCOPES = ['employee', 'project', 'labour', 'plant', 'office', 'property', 'land', 'organization'];
const STATUSES = ['Active', 'Expired', 'Cancelled', 'Pending Renewal'];

const EMPTY_FORM = {
  policy_type: '', policy_number: '', insurer_name: '', sum_insured: '', premium_amount: '',
  start_date: '', expiry_date: '', renewal_reminder_days: 30, status: 'Active', notes: '',
};

export default function ComplianceInsurance() {
  const [tab, setTab] = useUrlTab('tab', 'car');
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [modal, setModal] = useState(null);          // {mode:'create'|'edit', form, id?}
  const [enforceFor, setEnforceFor] = useState(null); // policy being enforced
  const [enforceScope, setEnforceScope] = useState({ apply_to: [], project_ids: [], entity_ids: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await apiGet('/compliance/insurance');
      setItems(r.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); apiGet('/projects').then(r => setProjects(normalizeProjects(r))).catch(() => {}); }, []);

  const tabItems = items.filter(p => policyTypeToTab(p.policy_type) === tab);
  const countFor = (key) => items.filter(p => policyTypeToTab(p.policy_type) === key).length;

  const openCreate = () => setModal({ mode: 'create', form: { ...EMPTY_FORM, policy_type: CANONICAL_POLICY_TYPE[tab] } });
  const openEdit = (p) => setModal({
    mode: 'edit', id: p.id,
    form: {
      policy_type: p.policy_type, policy_number: p.policy_number || '', insurer_name: p.insurer_name || '',
      sum_insured: p.sum_insured || '', premium_amount: p.premium_amount || '',
      start_date: (p.start_date || '').slice(0, 10), expiry_date: (p.expiry_date || '').slice(0, 10),
      renewal_reminder_days: p.renewal_reminder_days ?? 30, status: p.status || 'Active', notes: p.notes || '',
    },
  });

  const save = async () => {
    const f = modal.form;
    if (!f.policy_type) { toast.error('Policy type is required'); return; }
    setSaving(true);
    try {
      const body = { ...f, sum_insured: Number(f.sum_insured) || 0, premium_amount: Number(f.premium_amount) || 0, renewal_reminder_days: Number(f.renewal_reminder_days) || 30 };
      if (modal.mode === 'create') await apiPost('/compliance/insurance', body);
      else await apiPatch(`/compliance/insurance/${modal.id}`, body);
      toast.success(modal.mode === 'create' ? 'Policy added' : 'Policy updated');
      setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete policy ${p.policy_number || p.policy_type}?`)) return;
    try { await apiDel(`/compliance/insurance/${p.id}`); toast.success('Policy deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const confirmAi = async (p) => {
    try {
      await apiPatch(`/compliance/insurance/${p.id}`, { ai_suggested: false, status: 'Active' });
      toast.success('Policy confirmed as Active');
      load();
    } catch (e) { toast.error('Confirm failed'); }
  };

  const submitEnforce = async () => {
    try {
      await apiPost(`/compliance/insurance/${enforceFor.id}/enforce`, {
        apply_to: enforceScope.apply_to,
        project_ids: enforceScope.project_ids,
        entity_ids: enforceScope.entity_ids.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Policy enforced with selected scope');
      setEnforceFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enforce failed'); }
  };

  const uploadDoc = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try { await apiUpload(`/compliance/insurance/${p.id}/documents`, fd); toast.success('Document uploaded'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const setForm = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));
  const toggleScope = (s) => setEnforceScope(sc => ({
    ...sc, apply_to: sc.apply_to.includes(s) ? sc.apply_to.filter(x => x !== s) : [...sc.apply_to, s],
  }));

  return (
    <div data-testid="compliance-insurance-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People &amp; HR · Compliance</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Insurance Policies</h1>
          <p className="text-sm text-slate-600 mt-1">10 insurance pillars — manage policies, renewals, enforcement scope and documents.</p>
        </div>
        <button onClick={openCreate} className={`${BTN_PRIMARY} flex items-center gap-2`} data-testid="add-policy-btn">
          <Plus size={14} /> Add Policy
        </button>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-slate-200 mb-4" data-testid="insurance-type-tabs">
        {INSURANCE_TYPES.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`insurance-tab-${t.key}`}
                  className={`px-3 py-2 text-xs font-bold border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-sm text-[10px] ${tab === t.key ? 'bg-[#3b4872] text-white' : 'bg-slate-100 text-slate-500'}`}>{countFor(t.key)}</span>
          </button>
        ))}
      </div>

      {tab === 'wc' && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-800" data-testid="wc-info-banner">
          <Info size={14} className="mt-0.5 shrink-0" />
          This policy covers Site Employees and Workmen only. Head Office employees are covered under ESIC (Statutory Registers).
        </div>
      )}
      {tab === 'medical' && (
        <div className="mb-4 flex items-start gap-2 bg-slate-100 border border-slate-200 rounded-sm px-4 py-3 text-xs text-slate-600" data-testid="medical-info-banner">
          <Info size={14} className="mt-0.5 shrink-0" />
          Auto-application is disabled. Enforce manually when the organisation decides to roll out group medical coverage.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="compliance-insurance-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2">Policy Number</th>
              <th className="px-3 py-2">Insurer</th>
              <th className="px-3 py-2 text-right">Sum Insured</th>
              <th className="px-3 py-2">Start Date</th>
              <th className="px-3 py-2">Expiry Date</th>
              <th className="px-3 py-2">Renewal</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Enforced</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tabItems.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-slate-400">No policies of this type yet. Click “Add Policy” to create one.</td></tr>
            )}
            {tabItems.map(p => (
              <React.Fragment key={p.id}>
                <tr className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`insurance-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="text-slate-400 hover:text-slate-700" data-testid="expand-docs-btn">
                      {expanded === p.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {p.policy_number || <span className="text-slate-400">—</span>}
                    {p.ai_suggested && <div className="mt-1"><AiBadge /></div>}
                  </td>
                  <td className="px-3 py-2">{p.insurer_name || '—'}</td>
                  <td className="px-3 py-2 text-right">{formatINR(p.sum_insured, { dashIfNull: true })}</td>
                  <td className="px-3 py-2">{fmtDate(p.start_date)}</td>
                  <td className="px-3 py-2">{fmtDate(p.expiry_date)}</td>
                  <td className="px-3 py-2"><DaysBadge days={p.days_remaining} /></td>
                  <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-2"><EnforcedBadge enforced={p.enforced} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {p.ai_suggested && (
                      <button onClick={() => confirmAi(p)} className="text-emerald-700 hover:bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm text-[10px] font-bold mr-1" data-testid="confirm-ai-btn">
                        Confirm
                      </button>
                    )}
                    {!p.enforced && (
                      <button onClick={() => { setEnforceFor(p); setEnforceScope({ apply_to: [], project_ids: [], entity_ids: '' }); }}
                              className="text-[#3b4872] hover:bg-slate-100 border border-slate-300 px-2 py-1 rounded-sm text-[10px] font-bold mr-1" data-testid="enforce-btn">
                        <ShieldCheck size={11} className="inline -mt-0.5 mr-0.5" />Enforce
                      </button>
                    )}
                    <button onClick={() => openEdit(p)} className="text-slate-500 hover:text-[#3b4872] p-1" data-testid="edit-policy-btn"><Pencil size={13} /></button>
                    <button onClick={() => remove(p)} className="text-slate-400 hover:text-rose-600 p-1" data-testid="delete-policy-btn"><Trash2 size={13} /></button>
                  </td>
                </tr>
                {expanded === p.id && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={10} className="px-6 py-3" data-testid="policy-doc-vault">
                      <div className="flex items-center justify-between mb-2">
                        <span className="kpi-overline">Document Vault — {p.policy_type}</span>
                        <label className={`${BTN_SECONDARY} cursor-pointer flex items-center gap-1.5`}>
                          <Upload size={12} /> Upload
                          <input type="file" className="hidden" onChange={e => uploadDoc(p, e.target.files?.[0])} data-testid="upload-policy-doc-input" />
                        </label>
                      </div>
                      {(p.documents || []).length === 0
                        ? <div className="text-xs text-slate-400 py-2">No documents uploaded for this policy.</div>
                        : (
                          <div className="space-y-1">
                            {p.documents.map(d => (
                              <div key={d.id} className="flex items-center gap-3 text-xs bg-white border border-slate-200 rounded-sm px-3 py-2">
                                <a href={d.url} target="_blank" rel="noreferrer" className="font-medium text-[#3b4872] hover:underline flex-1 truncate">{d.filename}</a>
                                <span className="text-slate-400">{fmtDate(d.upload_date)}</span>
                                {d.project_id && <span className="text-slate-400">· {projects.find(pr => pr.id === d.project_id)?.name || d.project_id}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-sm border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="policy-modal">
            <h2 className="font-display text-lg font-bold mb-4">{modal.mode === 'create' ? 'Add Policy' : 'Edit Policy'}</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2 block">
                <span className="kpi-overline">Policy Type</span>
                <select value={modal.form.policy_type} onChange={e => setForm('policy_type', e.target.value)}
                        className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="policy-type-select">
                  {Object.values(CANONICAL_POLICY_TYPE).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="block"><span className="kpi-overline">Policy Number</span>
                <input value={modal.form.policy_number} onChange={e => setForm('policy_number', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="policy-number-input" /></label>
              <label className="block"><span className="kpi-overline">Insurer Name</span>
                <input value={modal.form.insurer_name} onChange={e => setForm('insurer_name', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="insurer-name-input" /></label>
              <label className="block"><span className="kpi-overline">Sum Insured (₹)</span>
                <input type="number" value={modal.form.sum_insured} onChange={e => setForm('sum_insured', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="sum-insured-input" /></label>
              <label className="block"><span className="kpi-overline">Premium (₹)</span>
                <input type="number" value={modal.form.premium_amount} onChange={e => setForm('premium_amount', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="premium-input" /></label>
              <label className="block"><span className="kpi-overline">Start Date</span>
                <input type="date" value={modal.form.start_date} onChange={e => setForm('start_date', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="start-date-input" /></label>
              <label className="block"><span className="kpi-overline">Expiry Date</span>
                <input type="date" value={modal.form.expiry_date} onChange={e => setForm('expiry_date', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="expiry-date-input" /></label>
              <label className="block"><span className="kpi-overline">Reminder Days</span>
                <input type="number" value={modal.form.renewal_reminder_days} onChange={e => setForm('renewal_reminder_days', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="reminder-days-input" /></label>
              <label className="block"><span className="kpi-overline">Status</span>
                <select value={modal.form.status} onChange={e => setForm('status', e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="status-select">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label className="col-span-2 block"><span className="kpi-overline">Notes</span>
                <textarea value={modal.form.notes} onChange={e => setForm('notes', e.target.value)} rows={2} className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="notes-input" /></label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setModal(null)} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={save} disabled={saving} className={BTN_PRIMARY} data-testid="save-policy-btn">{saving ? 'Saving…' : 'Save Policy'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Enforce modal */}
      {enforceFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEnforceFor(null)}>
          <div className="bg-white rounded-sm border border-slate-200 w-full max-w-md max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="enforce-modal">
            <h2 className="font-display text-lg font-bold mb-1">Enforce Policy</h2>
            <p className="text-xs text-slate-500 mb-4">{enforceFor.policy_type} · {enforceFor.policy_number || 'No number'}</p>
            <div className="kpi-overline mb-2">Apply To</div>
            <div className="grid grid-cols-2 gap-1.5 mb-4">
              {SCOPES.map(s => (
                <label key={s} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                  <input type="checkbox" checked={enforceScope.apply_to.includes(s)} onChange={() => toggleScope(s)} data-testid={`scope-${s}-checkbox`} />
                  {s}
                </label>
              ))}
            </div>
            {enforceScope.apply_to.includes('project') && (
              <div className="mb-4">
                <div className="kpi-overline mb-1">Projects</div>
                <select multiple value={enforceScope.project_ids}
                        onChange={e => setEnforceScope(sc => ({ ...sc, project_ids: Array.from(e.target.selectedOptions).map(o => o.value) }))}
                        className="w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm h-28" data-testid="enforce-projects-select">
                  {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                </select>
              </div>
            )}
            {enforceScope.apply_to.some(s => !['project', 'organization'].includes(s)) && (
              <label className="block mb-4"><span className="kpi-overline">Entity IDs (comma separated)</span>
                <input value={enforceScope.entity_ids} onChange={e => setEnforceScope(sc => ({ ...sc, entity_ids: e.target.value }))}
                       placeholder="e.g. EMP-001, PLT-014" className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid="enforce-entity-ids-input" /></label>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setEnforceFor(null)} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={submitEnforce} disabled={enforceScope.apply_to.length === 0} className={BTN_PRIMARY} data-testid="confirm-enforce-btn">Enforce</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
